import { useCallback, type ReactNode } from 'react'
import type { TemplateSection } from 'shared/api/templateService'
import type { PermitWorkflowTimeline } from 'modules/permit/api/permitService'
import {
  ALWAYS_EDITABLE_SECTION_ROLE_CODES,
  ALWAYS_EDITABLE_STATUS_CODES,
} from 'modules/permit/pages/permit-request/utils/pageHelpers'
import { WorkflowSectionsSection } from './WorkflowSectionsSection'

interface PermitWorkflowSectionsPanelProps {
  sections: TemplateSection[]
  isReadOnly: boolean
  canApproveCurrentStage: boolean
  doesSectionMatchActiveStage: (section: TemplateSection) => boolean
  currentUserRoleCodeCandidates?: string[]
  permitStatus?: string
  renderTemplateFieldInput: (
    field: TemplateSection['fields'][number],
    options?: { disabled?: boolean },
  ) => ReactNode
  workflowTimeline?: PermitWorkflowTimeline | null
  getValidationContainerClass?: (key?: string | null) => string
}

/**
 * Wraps WorkflowSectionsSection with the logic that decides whether each section is editable.
 * This keeps PermitRequestForm focused on data orchestration rather than conditional rendering details.
 */
export const PermitWorkflowSectionsPanel = ({
  sections,
  isReadOnly,
  canApproveCurrentStage,
  doesSectionMatchActiveStage,
  currentUserRoleCodeCandidates = [],
  permitStatus,
  renderTemplateFieldInput,
  workflowTimeline,
  getValidationContainerClass,
}: PermitWorkflowSectionsPanelProps) => {
  const canEditSection = useCallback(
    (section: TemplateSection) => {
      const sectionCode = section.code?.trim().toUpperCase() ?? ''
      if (
        permitStatus &&
        ALWAYS_EDITABLE_STATUS_CODES.includes(permitStatus) &&
        ALWAYS_EDITABLE_SECTION_ROLE_CODES.includes(sectionCode) &&
        currentUserRoleCodeCandidates.includes(sectionCode)
      ) {
        return true
      }

      // In approval flow, editor must only edit the current active stage section.
      if (canApproveCurrentStage) {
        return doesSectionMatchActiveStage(section)
      }

      // Requestor/edit flow (non-approval) keeps existing behavior.
      if (!isReadOnly) {
        return true
      }

      // Read-only viewer cannot edit any workflow section.
      return false
    },
    [
      isReadOnly,
      canApproveCurrentStage,
      doesSectionMatchActiveStage,
      currentUserRoleCodeCandidates,
      permitStatus,
    ],
  )

  return (
    <WorkflowSectionsSection
      sections={sections}
      isReadOnly={isReadOnly}
      canEditSection={canEditSection}
      renderTemplateFieldInput={renderTemplateFieldInput}
      workflowTimeline={workflowTimeline}
      getValidationContainerClass={getValidationContainerClass}
    />
  )
}
